import { callApiV2 } from "@mirage/service-dbx-api";
import { ONE_MINUTE_IN_MILLIS } from "@mirage/shared/util/constants";
import { register, unregister } from "@mirage/shared/util/jobs";
import type { Observable } from "rxjs";
import { BehaviorSubject } from "rxjs";
import { convertToAvailableModels } from "../utils/convertAvailableModels";

import type { GuaranteedKVStorage } from "@mirage/storage";
import type { AvailableModels } from "../types";

// This file contains implementation of the Conversations service specific to managing available models.

export interface AvailableModelsStorage {
  availableModels: AvailableModels;
}

const SYNC_AVAILABLE_MODELS_JOB_NAME = "conversations-available-models-sync";
const SYNC_INTERVAL_MS = ONE_MINUTE_IN_MILLIS * 60;

export function createAvailableModelsService(
  storage: GuaranteedKVStorage<AvailableModelsStorage>,
) {
  const availableModels$ = new BehaviorSubject<AvailableModels>({ models: [] });

  // initialize available models from storage if they exist
  storage
    .get("availableModels")
    .then((availableModels) => availableModels$.next(availableModels))
    .catch();

  async function loadAvailableModels() {
    const response = await callApiV2(
      "contextEngineConversationsListAvailableModels",
      {},
    );
    const availableModels = convertToAvailableModels(response);
    availableModels$.next(availableModels);
    await storage.set("availableModels", availableModels);
  }

  // sync available models periodically
  async function startSyncAvailableModels() {
    return register(
      SYNC_AVAILABLE_MODELS_JOB_NAME,
      SYNC_INTERVAL_MS,
      true,
      () => {
        return loadAvailableModels();
      },
    );
  }

  async function cancelSyncAvailableModels() {
    unregister(SYNC_AVAILABLE_MODELS_JOB_NAME);
  }

  function availableModels(): Observable<AvailableModels> {
    return availableModels$;
  }

  return {
    availableModels,
    startSyncAvailableModels,
    cancelSyncAvailableModels,
  };
}
